import React from 'react';
import { FormattedMessage } from 'react-intl';

import FileProgress from './file-progress.jsx';
import { bytesToHumanSize } from '../lib/strformat.js';
import { fetchAndDecrypt, isRefEncrypted } from '../lib/media-fetch.js';
import { isUrlRelative, sanitizeUrl } from '../lib/utils.js';

export default class Attachment extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      downloader: null,
      progress: 0
    };

    this.downloadFile = this.downloadFile.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  downloadFile(url, filename, mimetype) {
    if (!url) {
      this.props.onError("Invalid download URL '" + url + "'");
      return;
    }

    if (isRefEncrypted(this.props.entityData)) {
      // Encrypted: the SDK's download() saves the bytes straight to disk, which
      // would hand the user a ciphertext file. Fetch, decrypt, then save.
      this.downloadEncryptedFile(url, filename, mimetype);
      return;
    }

    // Legacy plaintext attachment: unchanged path.
    const downloader = this.props.tinode.getLargeFileHelper();
    this.setState({downloader: downloader});
    downloader.download(url, filename, mimetype,
      loaded => this.setState({progress: loaded / this.props.size}),
      err => this.props.onError(err, 'err')
    )
    .then(_ => this.setState({downloader: null, progress: 0}))
    .catch(err => {
      if (err) {
        this.props.onError("Error downloading file: " + err.message, 'err');
      }
      this.setState({downloader: null, progress: 0});
    });
  }

  // Download + decrypt an AOME1 attachment and hand the plaintext to the browser.
  downloadEncryptedFile(url, filename, mimetype) {
    const controller = typeof AbortController != 'undefined' ? new AbortController() : null;
    // Stands in for the SDK downloader so the progress bar and [cancel] work.
    this.setState({
      downloader: {cancel: () => controller && controller.abort()},
      progress: 0
    });

    fetchAndDecrypt(this.props.tinode.authorizeURL(url), this.props.entityData, {
      mimeType: mimetype,
      signal: controller ? controller.signal : undefined,
      // fetch() gives no transfer progress, so this tracks the decryption pass.
      onProgress: (loaded, total) => this.setState({progress: total > 0 ? loaded / total : 0})
    })
    .then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'file_attachment';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoking immediately can cancel the save in some browsers.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      this.setState({downloader: null, progress: 0});
    })
    .catch(err => {
      if (err && err.name != 'AbortError') {
        this.props.onError("Error downloading file: " + err.message, 'err');
      }
      this.setState({downloader: null, progress: 0});
    });
  }

  handleCancel() {
    if (this.props.uploading) {
      this.props.onCancelUpload();
    } else if (this.state.downloader) {
      this.state.downloader.cancel();
    }
  }

  render() {
    let filename = this.props.filename || 'file_attachment';
    if (filename.length > 36) {
      filename = filename.substr(0, 16) + '...' + filename.substr(-16);
    }
    let size = this.props.size > 0 ?
      <span className="small gray">({bytesToHumanSize(this.props.size)})</span> :
      null;

    // Detect if the download URL is relative or absolute.
    // If the URL is relative use LargeFileHelper to attach authentication
    // credentials to the request.
    let url, helperFunc;
    if (!this.props.uploading && !this.state.downloader && isUrlRelative(this.props.downloadUrl)) {
      // Relative URL. Use download helper.
      url = '#';
      helperFunc = (e) => {
        e.preventDefault();
        this.downloadFile(this.props.downloadUrl, this.props.filename, this.props.mimetype);
      };
    } else {
      url = sanitizeUrl(this.props.downloadUrl);
      helperFunc = null;
    }
    const downloadWidget = <><i className="material-icons">file_download</i> <FormattedMessage id="save_attachment"
      defaultMessage="save" description="Call to save an attachment" /></>;
    return (
      <div className="attachment">
        <div><i className="material-icons big gray">insert_drive_file</i></div>
        <div className="flex-column">
          <div>{filename} {size}</div>
          {this.props.uploading || this.state.downloader ?
            <FileProgress progress={this.props.uploading ? this.props.progress : this.state.progress}
              onCancel={this.handleCancel} />
            :
            <div>{url ?
              <a href={url} download={this.props.filename} onClick={helperFunc}>{downloadWidget}</a>
              :
              <span className="light-gray">{downloadWidget}</span>
            }</div>
          }
        </div>
      </div>
    );
  }
};
